import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
  PolicyOutlined as PolicyIcon,
  Refresh as RefreshIcon,
  RestoreOutlined as RestoreIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import { setSettings, setFieldListForSetting, addSetting, fetchApplicationSettings, fetchUIFields } from '../store/applicationSettingsSlice';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import type { ApplicationSetting, FieldSetting, FieldSize } from '../types/applicationSettings';

// ─── Size slider helpers ──────────────────────────────────────────────────────

const sizeMarks = [
  { value: 0 },
  { value: 1 },
  { value: 2 },
  { value: 3 },
];
const sizeToValue: Record<FieldSize, number> = { '1': 0, '2': 1, '3': 2, '4': 3 };
const valueToSize: Record<number, FieldSize> = { 0: '1', 1: '2', 2: '3', 3: '4' };

// ─── Dirty detection ──────────────────────────────────────────────────────────

function isFieldDirty(current: FieldSetting, original: FieldSetting | undefined): boolean {
  if (!original) return false;
  return (
    current.active !== original.active ||
    current.required !== original.required ||
    !!current.newline !== !!original.newline ||
    (sizeToValue[current.size] ?? 0) !== (sizeToValue[original.size] ?? 0) ||
    (current.pattern ?? '') !== (original.pattern ?? '') ||
    (current.errorMessage ?? '') !== (original.errorMessage ?? '')
  );
}

// ─── Type chip helper ─────────────────────────────────────────────────────────

function typeChipFor(type: FieldSetting['type']) {
  if (type === 'tag') return { label: 'Tag', color: '#e3f2fd', textColor: '#1565c0' };
  if (type === 'fixed_field') return { label: 'Default Field', color: '#f3e5f5', textColor: '#6a1b9a' };
  return { label: 'Custom Field', color: '#e8f5e9', textColor: '#2e7d32' };
}

// ─── Draggable row ────────────────────────────────────────────────────────────

interface DraggableRowProps {
  field: FieldSetting;
  originalField: FieldSetting | undefined;
  onFieldChange: (name: string, changes: Partial<FieldSetting>) => void;
  onReset: (name: string) => void;
  onOpenValidation: (field: FieldSetting) => void;
}

function DraggableRow({ field, originalField, onFieldChange, onReset, onOpenValidation }: DraggableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.name });

  const dirty = isFieldDirty(field, originalField);
  const chip = typeChipFor(field.type);
  const hasValidation = !!(field.pattern || field.errorMessage);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleActiveChange = (checked: boolean) => {
    onFieldChange(field.name, { active: checked, required: checked ? field.required : false });
  };

  const handleRequiredChange = (checked: boolean) => {
    onFieldChange(field.name, { required: checked });
  };

  const handleSizeChange = (_: Event, value: number | number[]) => {
    onFieldChange(field.name, { size: valueToSize[value as number] });
  };

  const stopPropagation = (e: React.PointerEvent | React.MouseEvent) => e.stopPropagation();

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: dirty ? 'rgba(255, 193, 7, 0.07)' : 'background.paper',
        '&:last-child': { borderBottom: 'none' },
        transition: 'background-color 0.2s',
      }}
    >
      {/* Drag handle — hidden for pinned fields */}
      <Box
        {...(field.pinned ? {} : listeners)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          cursor: field.pinned ? 'default' : isDragging ? 'grabbing' : 'grab',
          color: field.pinned ? 'transparent' : 'text.disabled',
          flexShrink: 0,
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, py: 1.5, pr: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Left: name + chip */}
        <Typography variant="body2" fontWeight={500} sx={{ flexShrink: 0 }}>
          {field.name}
        </Typography>
        <Chip
          label={chip.label}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem', bgcolor: chip.color, color: chip.textColor, fontWeight: 600, flexShrink: 0 }}
        />

        <Box sx={{ flex: 1 }} />

        {/* Right: size + checkboxes + icons — grouped so they wrap as one unit */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mt: '4px' }}>
            Size
          </Typography>
          <Slider
            value={sizeToValue[field.size] ?? 0}
            onChange={handleSizeChange}
            onPointerDown={stopPropagation}
            min={0}
            max={3}
            step={1}
            marks={sizeMarks}
            size="small"
            sx={{ width: 80, ml: 1, mr: 3 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={field.active}
                onChange={(e) => handleActiveChange(e.target.checked)}
                onPointerDown={stopPropagation}
                disabled={field.alwaysActive}
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography variant="caption">Active</Typography>}
            sx={{ m: 0, mr: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={field.required}
                onChange={(e) => handleRequiredChange(e.target.checked)}
                disabled={!field.active || field.alwaysRequired}
                onPointerDown={stopPropagation}
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography variant="caption">Required</Typography>}
            sx={{ m: 0, mr: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!field.newline}
                onChange={(e) => onFieldChange(field.name, { newline: e.target.checked })}
                disabled={!field.active}
                onPointerDown={stopPropagation}
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography variant="caption">New Line</Typography>}
            sx={{ m: 0, mr: 0.5 }}
          />
          <Tooltip title={hasValidation ? 'Validation rule set' : 'Set validation rule'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onOpenValidation(field); }}
              onPointerDown={stopPropagation}
              sx={{ color: hasValidation ? 'error.main' : 'text.secondary' }}
            >
              <PolicyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset to saved state">
            <span>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onReset(field.name); }}
                onPointerDown={stopPropagation}
                disabled={!dirty}
                sx={{ color: dirty ? 'warning.main' : 'text.disabled' }}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

// ─── New Profile Dialog ───────────────────────────────────────────────────────

const SYSTEM_DEFAULT_VALUE = '__system_default__';

interface NewProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (setting: ApplicationSetting, copyFromId: string) => void;
  settings: ApplicationSetting[];
  defaultCopyFromId: string;
}

function NewProfileDialog({ open, onClose, onCreated, settings, defaultCopyFromId }: NewProfileDialogProps) {
  const [name, setName] = useState('');
  const [copyFromId, setCopyFromId] = useState(SYSTEM_DEFAULT_VALUE);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
      setSubmitting(false);
      setCopyFromId(defaultCopyFromId || SYSTEM_DEFAULT_VALUE);
    }
  }, [open, defaultCopyFromId]);

  const handleAdd = async () => {
    setError('');
    setSubmitting(true);
    try {
      const sourceId = copyFromId === SYSTEM_DEFAULT_VALUE ? '' : copyFromId;
      const sourceSetting = settings.find((s) => s.settingId === sourceId);
      const payload: { settingName: string; active: boolean; settingJsonString?: string } = {
        settingName: name,
        active: false,
        ...(sourceSetting?.settingJsonString ? (() => {
        try {
          const blob = JSON.parse(sourceSetting.settingJsonString);
          delete blob.historyTrackingUnit;
          return { settingJsonString: JSON.stringify(blob) };
        } catch {
          return {};
        }
      })() : {}),
      };
      const response = await apiService.createApplicationSetting(payload);
      onCreated(response.data, sourceId);
      onClose();
    } catch (err: any) {
      setError(err?.jsonMessage?.settingName || err?.message || 'Failed to create profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Setting Profile</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField
            label="Profile Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) handleAdd(); }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Copy From</InputLabel>
            <Select
              value={copyFromId}
              label="Copy From"
              onChange={(e) => setCopyFromId(e.target.value)}
            >
              <MenuItem value={SYSTEM_DEFAULT_VALUE}>System Default</MenuItem>
              {settings.map((s) => (
                <MenuItem key={s.settingId} value={s.settingId}>
                  {s.settingName}{s.active ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={submitting || !name.trim()}>
          {submitting ? <CircularProgress size={18} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Validation Dialog ────────────────────────────────────────────────────────

interface ValidationDialogProps {
  open: boolean;
  field: FieldSetting | null;
  onClose: () => void;
  onSave: (name: string, pattern: string, errorMessage: string) => void;
}

function ValidationDialog({ open, field, onClose, onSave }: ValidationDialogProps) {
  const [pattern, setPattern] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open && field) {
      setPattern(field.pattern ?? '');
      setErrorMessage(field.errorMessage ?? '');
    }
  }, [open, field]);

  const handleSave = () => {
    if (!field) return;
    onSave(field.name, pattern, errorMessage);
    onClose();
  };

  if (!field) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Validation Rule — {field.name}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Regex Pattern"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. ^[A-Z]+$"
            autoFocus
          />
          <TextField
            label="Error Message"
            value={errorMessage}
            onChange={(e) => setErrorMessage(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. Must be uppercase letters only"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ApplicationSettings() {
  const dispatch = useAppDispatch();
  const { settings, fieldLists, isLoading, error } = useAppSelector((state) => state.applicationSettings);

  const [selectedSettingId, setSelectedSettingId] = useState<string>('');
  const [fieldList, setFieldList] = useState<FieldSetting[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  const [newProfileOpen, setNewProfileOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [validationField, setValidationField] = useState<FieldSetting | null>(null);
  const [validationOpen, setValidationOpen] = useState(false);

  // [TEMP] UI Fields debug modal
  const [uiFieldsOpen, setUiFieldsOpen] = useState(false);
  const [uiFieldsData, setUiFieldsData] = useState<any>(null);
  const [uiFieldsLoading, setUiFieldsLoading] = useState(false);
  const handleOpenUiFields = async () => {
    setUiFieldsOpen(true);
    setUiFieldsLoading(true);
    try {
      const res = await apiService.getUIFields();
      setUiFieldsData(res.data);
    } catch (err: any) {
      setUiFieldsData({ error: err?.message ?? 'Failed to load' });
    } finally {
      setUiFieldsLoading(false);
    }
  };

  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sync selectedSettingId and fieldList when settings/fieldLists change
  useEffect(() => {
    if (settings.length === 0) return;
    const target = settings.find((s) => s.active) ?? settings[0];
    setSelectedSettingId(target.settingId);
    setFieldList(fieldLists[target.settingId] ?? []);
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch merged/hydrated field list from backend dynamically on change
  useEffect(() => {
    if (!selectedSettingId) return;
    let cancelled = false;
    setLoadingFields(true);
    apiService.getFieldList(selectedSettingId)
      .then((res) => {
        if (!cancelled) {
          setFieldList(res.data ?? []);
        }
      })
      .catch((err: any) => {
        dispatch(showNotification({ message: err?.message ?? 'Failed to load fields', severity: 'error' }));
      })
      .finally(() => {
        if (!cancelled) setLoadingFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSettingId, dispatch]);

  const handleProfileChange = (settingId: string) => {
    setSelectedSettingId(settingId);
    setFieldList(fieldLists[settingId] ?? []);
  };

  const handleFieldChange = (name: string, changes: Partial<FieldSetting>) => {
    setFieldList((prev) => prev.map((f) => (f.name === name ? { ...f, ...changes } : f)));
  };

  const handleItemReset = (name: string) => {
    const original = (fieldLists[selectedSettingId] ?? []).find((f) => f.name === name);
    if (original) setFieldList((prev) => prev.map((f) => (f.name === name ? { ...original } : f)));
  };

  const handleOpenValidation = (field: FieldSetting) => {
    setValidationField(field);
    setValidationOpen(true);
  };

  const handleSaveValidation = (name: string, pattern: string, errorMessage: string) => {
    setFieldList((prev) =>
      prev.map((f) =>
        f.name === name
          ? {
              ...f,
              pattern: pattern || undefined,
              errorMessage: errorMessage || undefined,
              preventInvalidInput: !!(pattern || errorMessage),
            }
          : f
      )
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFieldList((items) => {
        const oldIndex = items.findIndex((f) => f.name === active.id);
        const newIndex = items.findIndex((f) => f.name === over.id);
        const pinnedCount = items.filter((f) => f.pinned).length;
        // Block: cannot drag a pinned field, cannot drag anything above pinned fields
        if (items[oldIndex].pinned || newIndex < pinnedCount) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleApply = async () => {
    if (!selectedSettingId) return;
    setSaving(true);
    try {
      const withSerials = fieldList.map((f, idx) => ({ ...f, serial: idx + 1 }));
      const [, activateRes] = await Promise.all([
        apiService.saveFieldList(selectedSettingId, withSerials),
        apiService.updateApplicationSetting(selectedSettingId, { active: true }),
      ]);
      dispatch(setFieldListForSetting({ settingId: selectedSettingId, fieldList: withSerials }));
      // Deactivate all others, activate selected
      dispatch(setSettings(
        settings.map((s) =>
          s.settingId === selectedSettingId
            ? activateRes.data
            : { ...s, active: false }
        )
      ));
      setFieldList(withSerials);
      dispatch(fetchUIFields());
      dispatch(showNotification({ message: 'Profile activated and field list saved.', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to apply settings', severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFieldList(fieldLists[selectedSettingId] ?? []);
  };

  const handleDelete = async () => {
    if (!selectedSettingId) return;
    setDeleting(true);
    try {
      await apiService.deleteApplicationSetting(selectedSettingId);
      setDeleteConfirmOpen(false);
      // Backend always activates another profile — reflect that in Redux
      const remaining = settings.filter((s) => s.settingId !== selectedSettingId);
      const next = remaining.find((s) => s.active) ?? remaining[0];
      if (next) {
        setSelectedSettingId(next.settingId);
        setFieldList(fieldLists[next.settingId] ?? []);
        dispatch(setSettings(remaining.map((s) => ({ ...s, active: s.settingId === next.settingId }))));
      } else {
        setSelectedSettingId('');
        setFieldList([]);
        dispatch(setSettings([]));
      }
      dispatch(showNotification({ message: 'Profile deleted.', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to delete profile', severity: 'error' }));
    } finally {
      setDeleting(false);
    }
  };

  const originalMap = Object.fromEntries((fieldLists[selectedSettingId] ?? []).map((f) => [f.name, f]));

  return (
    <DashboardLayout>
      {/* Outer: flex column filling the content area, escaping DashboardLayout's p:3 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)' }}>
      {/* Scrollable content with padding restored */}
      <Box sx={{ flexGrow: 1, p: 3, maxWidth: 900 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Employee Entry Settings
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => dispatch(fetchApplicationSettings())} disabled={isLoading} sx={{ mt: '2px' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {/* [TEMP] debug button */}
          <Button size="small" variant="outlined" onClick={handleOpenUiFields}>
            UI Fields JSON
          </Button>
        </Box>

        {/* Section 1 — Profile Selector Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FormControl sx={{ flex: 1 }} size="small" disabled={isLoading}>
            <InputLabel>Setting Profile</InputLabel>
            <Select
              value={selectedSettingId}
              label="Setting Profile"
              onChange={(e) => handleProfileChange(e.target.value)}
            >
              {settings.map((s) => (
                <MenuItem key={s.settingId} value={s.settingId}>
                  {s.settingName}{s.active ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => setNewProfileOpen(true)} disabled={isLoading}>
            New
          </Button>
        </Box>

        {/* Section 2 — Employee Entry Settings */}
        <Paper variant="outlined" sx={{ mb: 3, p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Employee Entry Settings
          </Typography>

          {/* Field List and Order */}
          <Paper variant="outlined">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2">Field List and Order</Typography>
              <IconButton size="small" disabled>
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {isLoading || loadingFields ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : fieldList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No fields configured. Select a profile or create one.
              </Typography>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fieldList.map((f) => f.name)} strategy={verticalListSortingStrategy}>
                  {fieldList.map((field) => (
                    <DraggableRow
                      key={field.name}
                      field={field}
                      originalField={originalMap[field.name]}
                      onFieldChange={handleFieldChange}
                      onReset={handleItemReset}
                      onOpenValidation={handleOpenValidation}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Paper>

      </Box>

      {/* Section 3 — Full-width action bar pinned at bottom */}
      <Paper elevation={6} sx={{ position: 'sticky', bottom: 0, px: 3, py: 1.5, display: 'flex', gap: 1, borderRadius: 0, mt: 'auto' }}>
        <Button variant="contained" onClick={handleApply} disabled={saving || !selectedSettingId}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Apply
        </Button>
        <Button variant="outlined" onClick={handleReset} disabled={!selectedSettingId}>
          Reset
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteConfirmOpen(true)}
          disabled={!selectedSettingId || saving}
        >
          Delete
        </Button>
      </Paper>
      </Box>

      {/* Dialogs */}
      <NewProfileDialog
        open={newProfileOpen}
        onClose={() => setNewProfileOpen(false)}
        settings={settings}
        defaultCopyFromId={selectedSettingId}
        onCreated={async (setting, copyFromId) => {
          setSelectedSettingId(setting.settingId);
          try {
            // Copy field list from source profile if selected
            if (copyFromId) {
              const sourceFieldList = fieldLists[copyFromId] ?? [];
              if (sourceFieldList.length > 0) {
                await apiService.saveFieldList(setting.settingId, sourceFieldList);
              }
            }
            // Fetch the merged field list for the new profile
            const fieldRes = await apiService.getFieldList(setting.settingId);
            const fetched = fieldRes.data ?? [];
            // Persist with serials + activate — same as Apply button
            const withSerials = fetched.map((f: FieldSetting, idx: number) => ({ ...f, serial: idx + 1 }));
            const [, activateRes] = await Promise.all([
              apiService.saveFieldList(setting.settingId, withSerials),
              apiService.updateApplicationSetting(setting.settingId, { active: true }),
            ]);
            dispatch(setFieldListForSetting({ settingId: setting.settingId, fieldList: withSerials }));
            setFieldList(withSerials);
            // Reflect activation in Redux: new profile active, all others inactive
            dispatch(setSettings(
              [...settings, activateRes.data].map((s) =>
                s.settingId === setting.settingId ? activateRes.data : { ...s, active: false }
              )
            ));
          } catch {
            dispatch(addSetting(setting));
            dispatch(setFieldListForSetting({ settingId: setting.settingId, fieldList: [] }));
            setFieldList([]);
          }
        }}
      />
      <ValidationDialog
        open={validationOpen}
        field={validationField}
        onClose={() => setValidationOpen(false)}
        onSave={handleSaveValidation}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Profile</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Delete <strong>{settings.find((s) => s.settingId === selectedSettingId)?.settingName}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* [TEMP] UI Fields debug modal */}
      <Dialog open={uiFieldsOpen} onClose={() => setUiFieldsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>UI Fields — /application-settings/ui-fields</DialogTitle>
        <DialogContent dividers>
          {uiFieldsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {JSON.stringify(uiFieldsData, null, 2)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUiFieldsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
